/**
 * Live market odds — Frontend-UI `MarketSseClient` on Socket-API SSE.
 *
 * `GET {VITE_SOCKET_BASE_URL}/v1/stream?channels=…` — public, no key
 * (Frontend `SOCKET_API_URL`, default same-origin proxy). Market-API blasts
 * LiveRatePayload; last tick is kept across reconnects so a drop never blanks
 * the ladder. Reconnect delay matches Lab RATE_SOCKET (50–300ms).
 */

import { useEffect, useRef, useState } from 'react'
import { getSocketBaseUrl, hasLiveSocket } from '@/api/socketClient'
import {
  liveOddsTickFromSse,
  marketRatesStreamPath,
  type LiveRatePayload,
} from '@/api/map/rates'
import { parseSseChunk } from '@/lib/sse'

export type MarketRatesStreamStatus = 'idle' | 'connecting' | 'open' | 'closed'

/** Frontend `RateSocketClient` reconnectionDelay / reconnectionDelayMax. */
const RETRY_MIN_MS = 50
const RETRY_MAX_MS = 300

export function useMarketRatesStream(marketId: string | undefined): {
  payload: LiveRatePayload | null
  status: MarketRatesStreamStatus
} {
  const [payload, setPayload] = useState<LiveRatePayload | null>(null)
  const [status, setStatus] = useState<MarketRatesStreamStatus>('idle')
  const payloadRef = useRef<LiveRatePayload | null>(null)
  payloadRef.current = payload

  useEffect(() => {
    const id = marketId?.trim() ?? ''
    setPayload(null)
    payloadRef.current = null

    if (!id || !hasLiveSocket()) {
      setStatus('idle')
      return
    }
    const base = getSocketBaseUrl()
    if (!base) {
      setStatus('idle')
      return
    }

    const ac = new AbortController()
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0

    const connect = async () => {
      if (cancelled) return
      setStatus('connecting')
      try {
        // Public subscribe — Frontend EventSource(withCredentials: false).
        // Socket-API CORS is credentials:false; never send Bearer / cookies.
        const response = await fetch(`${base}${marketRatesStreamPath(id)}`, {
          method: 'GET',
          headers: { Accept: 'text/event-stream' },
          credentials: 'omit',
          signal: ac.signal,
        })
        if (!response.ok || !response.body) {
          throw new Error(`rates-stream ${response.status}`)
        }
        setStatus('open')
        attempt = 0

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (!cancelled) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          buffer = parseSseChunk(buffer, (name, data) => {
            if (name === 'connected') {
              setStatus('open')
              return
            }
            const next = liveOddsTickFromSse(name, data, id, payloadRef.current)
            if (!next) return
            payloadRef.current = next
            setPayload(next)
          })
        }
        if (!cancelled) setStatus('closed')
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return
        setStatus('closed')
      }

      if (cancelled) return
      attempt += 1
      const delay = Math.min(RETRY_MAX_MS, RETRY_MIN_MS * 2 ** Math.min(attempt - 1, 3))
      retryTimer = setTimeout(connect, delay)
    }

    void connect()

    return () => {
      cancelled = true
      ac.abort()
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [marketId])

  return { payload, status }
}
